import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, BarChart3, Percent, Droplets, TrendingDown, BookOpen, ClipboardCheck, ChevronDown, ChevronUp, ArrowDownUp, Camera, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DatePicker from '@/components/DatePicker';
import JournalEntryModal from '@/components/JournalEntryModal';
import DripBuyToggleButton from '@/components/DripBuyToggleButton';
import SingleMultiShareSell from '@/components/SingleMultiShareSell';
import { ACCOUNT_TYPES, DEFAULT_ACCOUNT_TYPE } from '@/lib/accountTypes';
import { BROKERS } from '@/lib/brokers';
import AccountTypeModal, { AccountTypeBadge } from '@/components/AccountTypeModal';
import { dedupedGet } from '@/lib/dedupedGet';
import BrokerModal, { BrokerBadge } from '@/components/BrokerModal';
import { useActivePortfolioId } from '@/components/PortfolioSelector';
import { getSnapshot as getPortfolioSnap, hydrate as hydratePortfolioStore } from '@/lib/portfolioStore';
import { useIsReadOnlyAllView } from '@/lib/useIsReadOnlyAllView';
import AllViewReadOnlyBanner from '@/components/AllViewReadOnlyBanner';
import TradePreviewCard from '@/components/TradePreviewCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SummaryCard({ icon: Icon, label, value, subtitle, valueColor, testId }) {
  return (
    <div className="rounded-lg border bg-card p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon className="w-3 h-3 text-muted-foreground" />}
        <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      </div>
      <p className={`text-base font-bold font-mono ${valueColor || ''}`}>{value}</p>
      {subtitle && <p className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  );
}

export default function MyPortfolioTab({ stock, onPositionsChanged, onViewPortfolio, onOpenSnapshot, quickMode = false }) {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shares, setShares] = useState('');
  const [costPerShare, setCostPerShare] = useState('');
  const [datePurchased, setDatePurchased] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Cash source for the buy: outside (default) | inside | split.
  // When split, `insideAmount` is the dollars taken from internal
  // portfolio cash; the rest counts as a fresh contribution.
  const [cashSource, setCashSource] = useState('outside');
  const [insideAmount, setInsideAmount] = useState('');
  const [cashBalance, setCashBalance] = useState(0);
  // DRIP Buy flag — defaults to No. When Yes, the position gets a
  // water-drop badge in the StockBar so the user can see DRIP lots
  // at a glance, AND the cash source auto-flips to 'inside' since
  // DRIP money came from a dividend payout (already inside cash).
  const [isDrip, setIsDrip] = useState(false);
  const [acctType, setAcctType] = useState(DEFAULT_ACCOUNT_TYPE);
  // Round 92o++++++++N (Feb 2026): optional broker tag for new lots.
  // '' = Untagged (the default — no broker stamp on the row).
  const [broker, setBroker] = useState('');
  // Round 92o++++++++N++++++++++ (Jun 2026) — Plus multi-portfolio.
  // The active portfolio_id is what new lots get tagged with so they
  // land in the right group. We also seed the broker + account_type
  // pickers from the active portfolio_group so the user doesn't have
  // to set them twice (group-level defaults inherit to new lots).
  const { activeId: activePortfolioId } = useActivePortfolioId();
  // Round 92o++++++++N++++++++++ (Jun 2026) — Plus All-View read-only gate.
  // When the user is viewing "All Portfolios" (Plus tier, ≥1 named portfolio),
  // every buy/sell/DRIP action in this tab must be hidden. The variable was
  // referenced in render but never resolved → ReferenceError. Resolve here.
  const { isReadOnly: isAllReadOnly } = useIsReadOnlyAllView();
  // Round P108ad (Feb 2026) — trigger portfolio-store hydration on
  // mount. The store is only hydrated by PortfolioSelector, which
  // isn't mounted on pages like /?view=live (the Live stock table).
  // When a user clicks a ticker from those pages, MyPortfolioTab
  // renders BEFORE isPlus / activeId are known, so isAllReadOnly
  // defaults to false and the Add Position form appears (bug the
  // user reported). Calling hydrate() here (dedup'd inside the
  // store) guarantees the guard flips to true within one tick.
  useEffect(() => { hydratePortfolioStore(); }, []);
  // Resolve the active portfolio_group object (if any) so we can read
  // its broker / account_type. `null` for "All Portfolios" view.
  const activeGroup = useMemo(() => {
    if (!activePortfolioId) return null;
    const snap = getPortfolioSnap();
    return (snap?.portfolios || []).find((p) => p.portfolio_id === activePortfolioId) || null;
  }, [activePortfolioId]);
  // When the user switches the active portfolio, re-seed the form's
  // broker + account_type defaults to that group's settings. The
  // user can still override per-lot from the inline selects below;
  // this just removes the "guess the right value every buy" tax.
  useEffect(() => {
    if (activeGroup) {
      if (activeGroup.account_type) setAcctType(activeGroup.account_type);
      if (activeGroup.broker && activeGroup.broker !== 'other') {
        setBroker(activeGroup.broker);
      } else {
        setBroker('');
      }
    }
  }, [activeGroup]);
  // Round 71d (Feb 2026): "Show losses only" filter for the Your
  // Positions list. When ON, hides every lot whose cost basis is at
  // or below the current price. Useful especially in late December
  // when the user is doing a tax-loss harvest pass — they can scan a
  // ticker's individual lots without leaving the modal. The toggle
  // only renders when the user actually has multiple lots AND a
  // current price is available (otherwise the filter has nothing to
  // do).
  const [lossesOnly, setLossesOnly] = useState(false);
  // Round 84k Phase 5.6 (Feb 15, 2026) — collapse the Your Positions
  // list to 2 rows by default. `Show More` reveals up to 4 visible
  // rows with an internal scrollbar for the rest. Sorted newest-first
  // by default (most-recent purchase at top); a small sort button next
  // to "Your Positions" flips the order.
  const [positionsExpanded, setPositionsExpanded] = useState(false);
  const [positionsSortNewestFirst, setPositionsSortNewestFirst] = useState(true);
  // Round 71l (Feb 2026): post-buy / post-sell journal-suggest. After
  // a successful trade we fire a Sonner toast inviting the user to
  // capture their thesis. When they accept we open this modal with
  // the right entry_type + ticker + transaction context pre-filled.
  // Held in component state (not the Sonner action handler) so the
  // modal renders inside the React tree and can use full UI
  // primitives.
  const [journalPrefill, setJournalPrefill] = useState(null);

  const ticker = stock?.ticker;
  // Round 97a (Feb 2026) — Suppress all DRIP UI for non-dividend
  // stocks. Non-div stocks don't pay cash to reinvest, so the DRIP
  // toggle (in both the new-lot form and the existing-lot rows) is
  // meaningless. Dividend stocks behave 100% as before because the
  // check is `=== false`, not a loose falsy check — a missing
  // `pays_dividend` field still falls through to the dividend path.
  const isNonDiv = stock?.pays_dividend === false;
  // Round 97a (Feb 2026) — Defensive: if the modal switches to a
  // non-div stock while `isDrip` was previously set to true (e.g. the
  // user toggled it on a div stock then closed and re-opened on a
  // non-div), force it back to false so the hidden state can't
  // silently mark the new lot as a DRIP buy.
  useEffect(() => {
    if (isNonDiv && isDrip) setIsDrip(false);
  }, [isNonDiv, isDrip]);

  // Fetch positions. P95: `positionsRefreshTick` bumps after a
  // Multi-Share Sell so the lot list refetches (many lots change at once).
  const [positionsRefreshTick, setPositionsRefreshTick] = useState(0);
  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    axios.get(`${API}/portfolio/${ticker}/positions`, { withCredentials: true })
      .then(res => setPositions(res.data || []))
      .catch(() => setPositions([]))
      .finally(() => setLoading(false));
  }, [ticker, positionsRefreshTick]);

  // Round 95v (Jun 2026) — per-lot account-type + broker editors. Mirrors
  // the same flow PortfolioView.js already uses on the Portfolio page so
  // the user can change either field straight from the modal without
  // leaving for the lot list.
  const [acctEditing, setAcctEditing] = useState(null);
  const [brokerEditing, setBrokerEditing] = useState(null);

  async function _saveAccountType(pos, newType, opts = {}) {
    const scope = opts.scope || 'lot';
    const prev = pos.account_type;
    setPositions((arr) => arr.map((p) => (p.position_id === pos.position_id ? { ...p, account_type: newType } : p)));
    try {
      if (scope === 'portfolio') {
        const { data } = await axios.post(`${API}/portfolio/account-type/apply-all`, { account_type: newType, portfolio_id: activePortfolioId || null }, { withCredentials: true });
        const n = data?.modified_count ?? data?.matched_count ?? 0;
        toast.success(`Updated ${n} lot${n === 1 ? '' : 's'} portfolio-wide → ${(newType || 'Untagged').replace(/_/g, ' ')}`);
        window.dispatchEvent(new Event('portfolio-changed'));
        setPositionsRefreshTick((t) => t + 1);
        _promptRetagDividends(null, prev, newType);
        return;
      }
      const applyAll = scope === 'all';
      const { data } = await axios.patch(`${API}/portfolio/${ticker}/positions/${pos.position_id}/account-type`, { account_type: newType, apply_all: applyAll, portfolio_id: activePortfolioId || null }, { withCredentials: true });
      if (applyAll) {
        const n = data?.modified_count ?? data?.matched_count ?? 0;
        toast.success(`Updated ${n} ${ticker} lot${n === 1 ? '' : 's'} → ${(newType || 'Untagged').replace(/_/g, ' ')}`);
        setPositionsRefreshTick((t) => t + 1);
      } else {
        toast.success(`Account set to ${(newType || 'Untagged').replace(/_/g, ' ')}`);
      }
      _promptRetagDividends(ticker, prev, newType);
    } catch (e) {
      setPositions((arr) => arr.map((p) => (p.position_id === pos.position_id ? { ...p, account_type: prev } : p)));
      toast.error(e?.response?.data?.detail || 'Could not save account type');
    }
  }

  // Round P108w (Feb 2026) — After an account-type change, offer to
  // re-tag matching historical dividend cash tx so the NIIT tile on
  // the tax audit / year-end pages catches up. Scoped to the active
  // portfolio; ticker=null when the user picked "portfolio-wide"
  // scope. Silent no-op when count is 0 (nothing divergent).
  async function _promptRetagDividends(retagTicker, prev, newType) {
    if (!newType || newType === prev) return;
    try {
      const params = { new_account_type: newType };
      if (activePortfolioId) params.portfolio_id = activePortfolioId;
      if (retagTicker) params.ticker = retagTicker;
      const { data } = await axios.get(`${API}/portfolio/dividends/retag-preview`, { params, withCredentials: true });
      const n = Number(data?.count) || 0;
      if (n <= 0) return;
      const scopeCopy = retagTicker
        ? `${n} past dividend${n === 1 ? '' : 's'} for ${retagTicker}`
        : `${n} past dividend${n === 1 ? '' : 's'} in this portfolio`;
      const newLabel = (newType || 'Untagged').replace(/_/g, ' ');
      toast(`${scopeCopy} still tagged with the old account type. Re-tag as ${newLabel}?`, {
        duration: 15000,
        action: {
          label: 'Re-tag',
          onClick: async () => {
            try {
              const body = { new_account_type: newType };
              if (activePortfolioId) body.portfolio_id = activePortfolioId;
              if (retagTicker) body.ticker = retagTicker;
              const { data: res } = await axios.post(`${API}/portfolio/dividends/retag`, body, { withCredentials: true });
              const modified = Number(res?.modified_count) || 0;
              toast.success(`Re-tagged ${modified} past dividend${modified === 1 ? '' : 's'} → ${newLabel}. NIIT figures on tax pages will refresh.`);
              window.dispatchEvent(new Event('portfolio-changed'));
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Could not re-tag past dividends');
            }
          },
        },
      });
    } catch {
      // silent — the primary save succeeded, this is just a nudge
    }
  }

  async function _saveBroker(pos, newBroker, opts = {}) {
    const scope = opts.scope || 'lot';
    const prev = pos.broker;
    setPositions((arr) => arr.map((p) => (p.position_id === pos.position_id ? { ...p, broker: newBroker } : p)));
    try {
      if (scope === 'portfolio') {
        const { data } = await axios.post(`${API}/portfolio/broker/apply-all`, { broker: newBroker, portfolio_id: activePortfolioId || null }, { withCredentials: true });
        const n = data?.modified_count ?? data?.matched_count ?? 0;
        toast.success(`Updated ${n} lot${n === 1 ? '' : 's'} portfolio-wide → ${newBroker || 'Untagged'}`);
        window.dispatchEvent(new Event('portfolio-changed'));
        setPositionsRefreshTick((t) => t + 1);
        return;
      }
      const applyAll = scope === 'all';
      const { data } = await axios.patch(`${API}/portfolio/${ticker}/positions/${pos.position_id}/broker`, { broker: newBroker, apply_all: applyAll, portfolio_id: activePortfolioId || null }, { withCredentials: true });
      if (applyAll) {
        const n = data?.modified_count ?? data?.matched_count ?? 0;
        toast.success(`Updated ${n} ${ticker} lot${n === 1 ? '' : 's'} → ${newBroker || 'Untagged'}`);
        setPositionsRefreshTick((t) => t + 1);
      } else {
        toast.success(`Broker set to ${newBroker || 'Untagged'}`);
      }
    } catch (e) {
      setPositions((arr) => arr.map((p) => (p.position_id === pos.position_id ? { ...p, broker: prev } : p)));
      toast.error(e?.response?.data?.detail || 'Could not save broker');
    }
  }



  // Fetch user's cash balance — needed so the inside-cash slider knows
  // the upper bound and displays "$X available". Refreshes when the
  // form mounts and after a successful buy/sell.
  const reloadCashBalance = useCallback(() => {
    dedupedGet(`${API}/portfolio/summary`, { withCredentials: true })
      .then(res => setCashBalance(Number(res.data?.cash_balance) || 0))
      .catch(() => setCashBalance(0));
  }, []);
  useEffect(() => {
    if (!ticker) return;
    reloadCashBalance();
  }, [ticker, reloadCashBalance]);

  // Add position
  // Round 71l (Feb 2026): trigger the post-trade journal toast.
  // Distilled to one helper so Add and Sell flows share the same
  // copy + dismiss logic. The toast shows a "Journal it" pill that
  // opens JournalEntryModal with everything pre-populated; "Maybe
  // later" simply dismisses.
  const _suggestJournal = ({ kind, ticker: tkr, title, transaction_id, context_snapshot }) => {
    const description = kind === 'thesis_sell'
      ? 'Capture your sell thesis while it\'s fresh — regret-check your future self.'
      : 'Capture your buy thesis while it\'s fresh — your future self will thank you.';
    const toastId = toast(`📓 Journal this trade?`, {
      description,
      duration: 9000,
      action: {
        label: 'Journal it',
        onClick: () => {
          setJournalPrefill({
            entry_type: kind,
            ticker: tkr,
            title,
            transaction_id,
            context_snapshot,
            body: '',
          });
          toast.dismiss(toastId);
        },
      },
      cancel: {
        label: 'Maybe later',
        onClick: () => toast.dismiss(toastId),
      },
    });
  };

  const handleAdd = async () => {
    if (!shares || Number(shares) <= 0) return;
    // Round 71m (Feb 2026): the buy form input now allows up to 4
    // decimal places (matches the glossary promise). Defensive client
    // round here in case anyone pastes a 6-decimal Robinhood value
    // into the field — the backend rounds too, so the only purpose of
    // this extra trim is to keep the UI's calculated total cost in
    // sync with what actually persists.
    const sharesRounded = Math.round(Number(shares) * 1e4) / 1e4;
    // Round to cents to mirror the backend (brokers fill in whole-cent
    // notional, so 0.0705 × $31.65 = $2.231325 must count as $2.23).
    const totalCost = Math.round(sharesRounded * (Number(costPerShare) || 0) * 100) / 100;
    let inside = 0;
    // DRIP mode: backend handles it as a special "drip" cash_source —
    // doesn't touch cash balance, doesn't count as contribution.
    // Don't validate cash_balance for DRIP buys (the user might be
    // brand new with $0 cash logged).
    if (!isDrip) {
      if (cashSource === 'inside') inside = totalCost;
      else if (cashSource === 'split') inside = Math.max(0, Math.min(totalCost, Number(insideAmount) || 0));
      if ((cashSource === 'inside' || cashSource === 'split') && inside > cashBalance + 1e-6) {
        toast.error(`Not enough portfolio cash. $${inside.toFixed(2)} requested, only $${cashBalance.toFixed(2)} available.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/portfolio/${ticker}/positions`, {
        shares: sharesRounded,
        cost_per_share: Number(costPerShare) || 0,
        date_purchased: datePurchased || new Date().toISOString().split('T')[0],
        cash_source: cashSource,
        inside_amount: cashSource === 'split' ? inside : 0,
        is_drip: isDrip,
        account_type: acctType,
        broker: broker || null,
        // Round 92o++++++++N++++++++++ (Jun 2026) — Plus multi-portfolio.
        // Tag the new lot with the currently active portfolio_id so
        // it lands in the right group. Null falls back server-side
        // to the user's persisted active portfolio (legacy clients).
        portfolio_id: activePortfolioId || null,
      }, { withCredentials: true });
      setPositions(prev => [data, ...prev]);
      setShares('');
      setCostPerShare('');
      setDatePurchased('');
      setCashSource('outside');
      setInsideAmount('');
      setIsDrip(false);
      // Round 92o++++++++N++++++++++ (Jun 2026) — Reset back to the
      // active portfolio_group's defaults instead of the hard-coded
      // "taxable / untagged" so successive buys in the same group
      // keep their inherited tag.
      setAcctType(activeGroup?.account_type || DEFAULT_ACCOUNT_TYPE);
      setBroker(activeGroup?.broker && activeGroup.broker !== 'other' ? activeGroup.broker : '');
      reloadCashBalance();
      onPositionsChanged?.();
      window.dispatchEvent(new Event('cash-changed'));
      // Round 71l: prompt journaling so users build the habit of
      // writing their thesis BEFORE outcomes are known. Toast
      // dismisses on its own; "Journal it" opens the entry modal
      // pre-filled with the trade context.
      _suggestJournal({
        kind: 'thesis_add',
        ticker,
        title: `Bought ${data.shares || shares} ${ticker} @ $${(data.cost_per_share || costPerShare)}`,
        transaction_id: data.position_id || null,
        context_snapshot: {
          shares: Number(data.shares || shares),
          cost_per_share: Number(data.cost_per_share || costPerShare) || 0,
          date_purchased: data.date_purchased || datePurchased || new Date().toISOString().split('T')[0],
          cash_source: cashSource,
          is_drip: isDrip,
          stock_price_at_entry: stock?.price ?? null,
          dividend_yield_at_entry: stock?.dividend_yield ?? null,
        },
      });
    } catch (err) {
      // Surface portfolio-cap rejections so the user knows why their
      // position didn't save. Backend returns 400 with a `detail` string
      // like "Portfolio limit of 20 stocks reached. ..." when full.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 400 && detail) {
        toast.error(detail);
      }
    }
    setSubmitting(false);
  };

  // Remove position
  const handleRemove = async (positionId) => {
    try {
      await axios.delete(`${API}/portfolio/${ticker}/positions/${positionId}`, { withCredentials: true });
      setPositions(prev => prev.filter(p => p.position_id !== positionId));
      onPositionsChanged?.();
    } catch {}
  };

  // Sell (partial) shares
  const [sellingId, setSellingId] = useState(null);
  const [sellShares, setSellShares] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [sellDate, setSellDate] = useState('');
  const [sellError, setSellError] = useState('');
  const [sellSubmitting, setSellSubmitting] = useState(false);
  // Round 71e (Feb 2026): tax-harvest flag for the current sell form.
  // Pre-set when the user clicks "Harvest this lot" on a loss row,
  // editable on the form (checkbox) so anyone can tag any sell.
  // Captured at openSell() time so the harvest_cost_basis the
  // backend records reflects the lot's actual cost basis at that
  // moment, even if the user later edits the share count.
  const [sellTaxHarvest, setSellTaxHarvest] = useState(false);
  const [sellHarvestCostBasis, setSellHarvestCostBasis] = useState(null);

  const openSell = (pos, opts = {}) => {
    setSellingId(pos.position_id);
    setSellShares(String(pos.shares));
    setSellPrice(stock?.price ? String(stock.price.toFixed(2)) : '');
    setSellDate(new Date().toISOString().split('T')[0]);
    setSellError('');
    setSellTaxHarvest(!!opts.taxHarvest);
    setSellHarvestCostBasis(opts.taxHarvest ? Number(pos.cost_per_share || 0) : null);
  };
  const closeSell = () => {
    setSellingId(null);
    setSellShares('');
    setSellPrice('');
    setSellDate('');
    setSellError('');
    setSellTaxHarvest(false);
    setSellHarvestCostBasis(null);
  };

  const handleSell = async (pos) => {
    const n = Math.round(Number(sellShares) * 1e4) / 1e4;
    const p = Number(sellPrice);
    if (!n || n <= 0) { setSellError('Enter shares to sell'); return; }
    if (n > pos.shares + 1e-9) { setSellError(`Only ${pos.shares} shares available`); return; }
    if (!p || p <= 0) { setSellError('Enter a sale price'); return; }
    setSellSubmitting(true);
    setSellError('');
    try {
      const { data } = await axios.post(
        `${API}/portfolio/${ticker}/positions/${pos.position_id}/sell`,
        {
          shares: n,
          sale_price: p,
          sale_date: sellDate || new Date().toISOString().split('T')[0],
          // Tax-harvest flag — defaults false. When true the backend
          // also stores realised_loss + harvest_cost_basis on the
          // transaction so the year-end harvest report can render
          // without re-deriving anything.
          tax_harvest: sellTaxHarvest,
          harvest_cost_basis: sellTaxHarvest
            ? (sellHarvestCostBasis ?? Number(pos.cost_per_share || 0))
            : null,
        },
        { withCredentials: true }
      );
      if (data.position) {
        setPositions(prev => prev.map(x => x.position_id === pos.position_id ? data.position : x));
      } else {
        setPositions(prev => prev.filter(x => x.position_id !== pos.position_id));
      }
      closeSell();
      onPositionsChanged?.();
      window.dispatchEvent(new Event('cash-changed'));
      // Round 71l: post-sell thesis prompt. Mention the realised P&L
      // in the suggested title so the user has a starting hook.
      const realisedDollar = (p - (pos.cost_per_share || 0)) * n;
      _suggestJournal({
        kind: 'thesis_sell',
        ticker,
        title: `Sold ${n} ${ticker} @ $${p.toFixed(2)} · ${realisedDollar >= 0 ? '+' : ''}$${realisedDollar.toFixed(2)} realised`,
        transaction_id: pos.position_id,
        context_snapshot: {
          shares: n,
          sale_price: p,
          sale_date: sellDate || new Date().toISOString().split('T')[0],
          cost_per_share: Number(pos.cost_per_share || 0),
          realised_gain_loss: Number(realisedDollar.toFixed(2)),
          tax_harvest: !!sellTaxHarvest,
        },
      });
    } catch (e) {
      setSellError(e?.response?.data?.detail || 'Failed to sell');
    }
    setSellSubmitting(false);
  };


  // Calculate metrics
  const metrics = useMemo(() => {
    if (!stock) return null;

    const allPositions = positions;
    const price = stock.price || 0;
    const divRate = stock.dividend_rate || 0;
    const divYield = stock.dividend_yield || 0;
    const freq = stock.frequency || 'Quarterly';
    const freqNum = freq === 'Monthly' ? 12 : freq === 'Quarterly' ? 4 : freq === 'Semi-Annually' ? 2 : freq === 'Annually' ? 1 : 4;

    // Include form input as preview
    const formShares = Number(shares) || 0;
    const formCost = Number(costPerShare) || 0;

    const totalShares = allPositions.reduce((s, p) => s + (p.shares || 0), 0) + formShares;
    const totalCost = allPositions.reduce((s, p) => s + (p.shares || 0) * (p.cost_per_share || 0), 0) + (formShares * formCost);

    const avgCost = totalShares > 0 ? totalCost / totalShares : 0;
    const currentValue = totalShares * price;
    const gainLoss = currentValue - totalCost;
    const gainLossPct = totalCost > 0 ? (gainLoss / totalCost * 100) : 0;
    const annualIncome = totalShares * divRate;
    const monthlyIncome = annualIncome / 12;
    const perDividend = totalShares > 0 ? divRate / freqNum : 0;
    const yieldOnCost = totalCost > 0 ? (annualIncome / totalCost * 100) : 0;

    return {
      totalShares,
      avgCost,
      totalCost,
      currentValue,
      gainLoss,
      gainLossPct,
      annualIncome,
      monthlyIncome,
      perDividend,
      yieldOnCost,
      freq,
      price,
      divRate,
      divYield,
    };
  }, [positions, stock, shares, costPerShare]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  return (
    <div className="space-y-3" data-testid="my-portfolio-tab">
      {/* Existing Positions */}
      {!quickMode && positions.length > 0 && (() => {
        // Compute loss-eligible status per lot (current price < cost
        // basis). Done up here so we can decide whether to render the
        // toggle AND filter the rendered list off the same data.
        const currentPrice = Number(stock?.price) || 0;
        const lossLots = currentPrice > 0
          ? positions.filter(p => Number(p.cost_per_share || 0) > currentPrice)
          : [];
        const baseList = lossesOnly && currentPrice > 0
          ? lossLots
          : positions;
        // Round 84k Phase 5.6 (Feb 15, 2026) — sort the list by
        // purchase date so the most recent (or oldest) lot sits at
        // the top, per user request. Sort is stable: equal dates
        // preserve original insertion order. Missing dates sort last
        // regardless of direction.
        const sortedList = [...baseList].sort((a, b) => {
          const ad = a.date_purchased || '';
          const bd = b.date_purchased || '';
          if (!ad && !bd) return 0;
          if (!ad) return 1;
          if (!bd) return -1;
          return positionsSortNewestFirst ? bd.localeCompare(ad) : ad.localeCompare(bd);
        });
        // Collapsed = top 2 rows only. Expanded = up to 4 visible
        // with internal scroll if more exist. The 4-row visible cap
        // is enforced via max-height + overflow-y on the wrapper
        // (each row is ~64px tall on desktop incl. borders → 256px).
        const COLLAPSED_COUNT = 2;
        const EXPANDED_VISIBLE_COUNT = 4;
        const canCollapse = sortedList.length > COLLAPSED_COUNT;
        const visiblePositions = (positionsExpanded || !canCollapse)
          ? sortedList
          : sortedList.slice(0, COLLAPSED_COUNT);
        const showScroller = positionsExpanded && sortedList.length > EXPANDED_VISIBLE_COUNT;
        const showLossToggle = positions.length > 1 && lossLots.length > 0 && currentPrice > 0;
        return (
        <div className="rounded-lg border bg-card" data-testid="positions-list">
          <div className="p-3 pb-1 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold">Your Positions</h3>
              {sortedList.length > 1 && (
                <button
                  type="button"
                  onClick={() => setPositionsSortNewestFirst(v => !v)}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-muted-foreground hover:bg-muted/70 hover:text-foreground transition-colors"
                  data-testid="positions-sort-toggle"
                  aria-label={`Sort by ${positionsSortNewestFirst ? 'oldest' : 'newest'} purchase first`}
                  title={positionsSortNewestFirst
                    ? 'Sorted: newest purchase first. Click to flip to oldest first.'
                    : 'Sorted: oldest purchase first. Click to flip to newest first.'}
                >
                  <ArrowDownUp className="w-2.5 h-2.5" />
                  {positionsSortNewestFirst ? 'Newest' : 'Oldest'}
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {showLossToggle && (
                <button
                  type="button"
                  onClick={() => setLossesOnly(v => !v)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                    lossesOnly
                      ? 'bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-300 ring-1 ring-rose-400/40'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70 hover:text-foreground'
                  }`}
                  data-testid="positions-losses-only-toggle"
                  aria-pressed={lossesOnly}
                  title={lossesOnly
                    ? 'Showing only lots whose current price is below their cost basis. Click to show all positions again.'
                    : `Hide profitable lots — show only the ${lossLots.length} loss lot${lossLots.length === 1 ? '' : 's'} eligible for tax-loss harvest review.`}
                >
                  <TrendingDown className="w-3 h-3" />
                  {lossesOnly
                    ? `Showing ${lossLots.length} loss lot${lossLots.length === 1 ? '' : 's'}`
                    : `Show losses only (${lossLots.length})`}
                </button>
              )}
              {onViewPortfolio && (
                <button
                  onClick={onViewPortfolio}
                  className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors px-2 py-1 rounded-md hover:bg-primary/10"
                  data-testid="view-portfolio-btn"
                >
                  View Portfolio
                </button>
              )}
            </div>
          </div>
          {/* P95 (Jun 2026) — Single Multi-Share Sell: top row of the
              owned-lots table. Same widget as the Portfolio page row
              expansion; hidden in the read-only All-Portfolios view. */}
          {!isAllReadOnly && (
            <SingleMultiShareSell
              ticker={ticker}
              totalShares={positions.reduce((s, p) => s + (p.shares || 0), 0)}
              currentPrice={stock?.price}
              onSold={() => {
                setPositionsRefreshTick((t) => t + 1);
                onPositionsChanged?.();
              }}
            />
          )}
          {visiblePositions.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-muted-foreground border-t" data-testid="positions-losses-empty">
              All your {ticker} lots are currently profitable — nothing to harvest here. Toggle off to see your other lots.
            </div>
          ) : (
          <div
            className={showScroller ? 'max-h-[256px] overflow-y-auto' : ''}
            data-testid="positions-list-scroller"
          >
          {visiblePositions.map((pos) => (
            <div key={pos.position_id} className="border-t" data-testid="position-row">
              {/* Mobile-first layout: shares stacked above "Purchased"
                  on narrow screens, Total Value on the right. Buttons
                  flow to a second row on phone so they don't steal
                  width from the share count column.

                  Previously the row used a 3-col grid with the buttons
                  in `auto`, which on phone crushed the shares column
                  to zero and truncated "10 shares @ $105.00" out of
                  sight entirely. */}
              <div className="flex items-center justify-between gap-3 px-3 py-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                    {!isNonDiv && (isAllReadOnly ? (
                      <Droplets className={`w-4 h-4 ${pos.is_drip ? 'text-sky-500' : 'text-muted-foreground/40'}`} />
                    ) : (
                      <DripBuyToggleButton
                        ticker={ticker}
                        positionId={pos.position_id}
                        isDrip={!!pos.is_drip}
                        size="w-4 h-4"
                        testIdSuffix={`modal-${pos.position_id}`}
                        onMarked={(updated) => {
                          if (!updated) return;
                          setPositions(prev => prev.map(p => p.position_id === pos.position_id ? { ...p, ...updated } : p));
                        }}
                      />
                    ))}
                    {pos.shares} Shares
                    <span className="font-mono font-normal text-muted-foreground ml-1.5">@ ${pos.cost_per_share?.toFixed(2)}</span>
                  </p>
                  <p className="text-[10px] text-muted-foreground">Purchased: {formatDate(pos.date_purchased)}</p>
                  {/* Round 95v (Jun 2026) — clickable account-type +
                      broker pills, same UI as the Portfolio page lot
                      rows. Read-only mode renders the badges as
                      non-clickable static chips so view-only
                      portfolios still see the tax/broker context. */}
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap" data-testid={`lot-tags-${pos.position_id}`}>
                    <AccountTypeBadge
                      value={pos.account_type}
                      onClick={isAllReadOnly ? null : () => setAcctEditing(pos)}
                      testId={`acct-badge-${pos.position_id}`}
                    />
                    <BrokerBadge
                      value={pos.broker}
                      onClick={isAllReadOnly ? null : () => setBrokerEditing(pos)}
                      testId={`broker-badge-${pos.position_id}`}
                    />
                  </div>
                </div>
                <div className="text-right shrink-0" data-testid="position-total-value">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-none">Total Value</p>
                  <p className="text-sm font-bold font-mono mt-0.5 tabular-nums">${(Number(pos.shares) * Number(pos.cost_per_share || 0)).toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-2 basis-full sm:basis-auto justify-end flex-wrap">
                  {(() => {
                    if (isAllReadOnly) return null;
                    // Show the "Harvest this lot" shortcut only when:
                    //   • a current price is available
                    //   • the lot is at a loss right now
                    //   • the user hasn't already opened the sell form for it
                    const cur = Number(stock?.price) || 0;
                    const cps = Number(pos.cost_per_share || 0);
                    const isLoss = cur > 0 && cps > cur;
                    if (!isLoss || sellingId === pos.position_id) return null;
                    const realised = Math.abs((cps - cur) * Number(pos.shares || 0));
                    return (
                      <button
                        onClick={() => openSell(pos, { taxHarvest: true })}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-950/60 transition-colors ring-1 ring-rose-300/40 dark:ring-rose-700/40"
                        data-testid="harvest-lot-btn"
                        title={`Pre-fill the Sell form with all ${pos.shares} shares of this loss lot, tagged as a tax-loss harvest. You still pick where the cash goes (default: stays in portfolio cash). Estimated realised loss at current price: -$${realised.toFixed(2)}.`}
                      >
                        <TrendingDown className="w-3 h-3" />
                        Harvest this lot
                      </button>
                    );
                  })()}
                  {!isAllReadOnly && (
                    <>
                      <button
                        onClick={() => (sellingId === pos.position_id ? closeSell() : openSell(pos))}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-950/60 transition-colors"
                        data-testid="sell-position-btn"
                      >
                        {sellingId === pos.position_id ? 'Cancel' : 'Sell Shares'}
                      </button>
                      <button
                        onClick={() => handleRemove(pos.position_id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/60 transition-colors"
                        data-testid="remove-position-btn"
                        title="Remove from portfolio (no cash recorded)"
                      >
                        Remove from Portfolio
                      </button>
                    </>
                  )}
                </div>
              </div>
              {sellingId === pos.position_id && (
                <div className="px-3 pb-3 bg-muted/30 border-t" data-testid="sell-position-form">
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground block mb-0.5">Shares</label>
                      <input
                        type="number"
                        min="0"
                        max={pos.shares}
                        step="0.0001"
                        value={sellShares}
                        onChange={(e) => setSellShares(e.target.value)}
                        className="w-full px-2 py-1 text-xs rounded border bg-background"
                        data-testid="sell-shares-input"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground block mb-0.5">Sale $/share</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={sellPrice}
                        onChange={(e) => setSellPrice(e.target.value)}
                        className="w-full px-2 py-1 text-xs rounded border bg-background"
                        data-testid="sell-price-input"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground block mb-0.5">Sale Date</label>
                      <input
                        type="date"
                        value={sellDate}
                        onChange={(e) => setSellDate(e.target.value)}
                        className="w-full px-2 py-1 text-xs rounded border bg-background"
                        data-testid="sell-date-input"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                    <p className="text-[10px] text-muted-foreground">
                      Proceeds: <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                        ${((Number(sellShares) || 0) * (Number(sellPrice) || 0)).toFixed(2)}
                      </span> → added to cash
                    </p>
                    <Button
                      size="sm"
                      onClick={() => handleSell(pos)}
                      disabled={sellSubmitting}
                      className={`h-7 text-xs ${sellTaxHarvest ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                      data-testid="sell-confirm-btn"
                    >
                      {sellSubmitting
                        ? (sellTaxHarvest ? 'Harvesting...' : 'Selling...')
                        : (sellTaxHarvest ? 'Confirm Harvest' : 'Confirm Sell')}
                    </Button>
                  </div>
                  {/* Tax-harvest tag — visible on every sell form. Pre-checked
                      when the user clicked "Harvest this lot" but always
                      editable. Doesn't change cash flow; it just stamps
                      the resulting transaction so the year-end harvest
                      report can pull it. */}
                  <div className="mt-2 pt-2 border-t border-border/60">
                    <label className="flex items-start gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={sellTaxHarvest}
                        onChange={(e) => {
                          setSellTaxHarvest(e.target.checked);
                          if (e.target.checked && sellHarvestCostBasis == null) {
                            setSellHarvestCostBasis(Number(pos.cost_per_share || 0));
                          }
                        }}
                        className="mt-0.5"
                        data-testid="sell-tax-harvest-checkbox"
                      />
                      <div className="flex-1">
                        <div className="text-[11px] font-semibold text-foreground flex items-center gap-1.5">
                          <TrendingDown className="w-3 h-3 text-rose-500" />
                          Tag as tax-loss harvest
                        </div>
                        {sellTaxHarvest && (() => {
                          const cps = sellHarvestCostBasis ?? Number(pos.cost_per_share || 0);
                          const proceeds = (Number(sellShares) || 0) * (Number(sellPrice) || 0);
                          const realised = proceeds - (Number(sellShares) || 0) * cps;
                          return (
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              Estimated realised{' '}
                              <span className={`font-mono font-bold ${realised < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                {realised < 0 ? 'loss' : 'gain'} of ${Math.abs(realised).toFixed(2)}
                              </span>
                              {' '}(cost basis ${cps.toFixed(2)}/sh × {Number(sellShares).toFixed(4)} sh = ${(cps * Number(sellShares)).toFixed(2)} basis vs ${proceeds.toFixed(2)} proceeds). Cash destination is your choice — proceeds go to portfolio cash by default and you can move them out later. The tag itself just lets you pull a year-end harvest report for your tax filing.
                            </p>
                          );
                        })()}
                        {!sellTaxHarvest && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            Optional. Check this if you\'re selling at a loss specifically to lock in a deductible capital loss. Your cash destination doesn\'t change — this just stamps the transaction so the year-end harvest report includes it.
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                  {sellError && <p className="mt-1 text-[10px] text-red-500" data-testid="sell-error">{sellError}</p>}
                </div>
              )}
            </div>
          ))}
          </div>
          )}
          {canCollapse && (
            <button
              type="button"
              onClick={() => setPositionsExpanded(v => !v)}
              className="w-full px-3 py-2 border-t text-[11px] font-bold uppercase tracking-wider text-muted-foreground hover:text-violet-700 dark:hover:text-violet-300 hover:bg-violet-50/40 dark:hover:bg-violet-950/20 transition-colors flex items-center justify-center gap-2"
              data-testid="positions-expand-toggle"
              aria-expanded={positionsExpanded}
            >
              {positionsExpanded ? (
                <>
                  <ChevronUp className="w-3 h-3" />
                  <span>Show Less</span>
                  <ChevronUp className="w-3 h-3" />
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" />
                  <span>Show More ({sortedList.length - COLLAPSED_COUNT} more)</span>
                  <ChevronDown className="w-3 h-3" />
                </>
              )}
            </button>
          )}
          {positions.length > 1 && (
            <div className="px-3 py-2 border-t text-xs text-muted-foreground font-medium" data-testid="positions-total">
              Total: {metrics?.totalShares.toFixed(2)} shares ~ ${metrics?.avgCost.toFixed(2)} avg
            </div>
          )}
        </div>
        );
      })()}

      {/* Round 96k (Jul 2026) — "Would this trade help my portfolio?"
          preview widget. Shows a live before-vs-after projection of
          the user's Portfolio Blend if they were to buy N shares of
          this ticker at a given price. Self-hides when the user
          already owns the ticker in the current portfolio (per user
          request — once the tab fills with lot rows there's no
          room). Positioned at the top of the tab per user spec. */}
      {!isAllReadOnly && !quickMode && (
        <TradePreviewCard
          ticker={ticker}
          currentPrice={Number(stock?.price ?? 0)}
          dividendYield={Number(stock?.dividend_yield ?? 0)}
          dividendCagr5y={Number(stock?.dividend_cagr_5y ?? stock?.dividend_cagr_3y ?? NaN)}
          ownsTicker={positions && positions.length > 0}
          portfolioId={activePortfolioId}
          onApplyTrade={({ shares: sh, price: pr }) => {
            // Round 96x (Jul 2026) — hydrate the Add Position form
            // below with whatever the user just modeled. Scrolls the
            // form into view so users see the pre-fill happen.
            if (sh > 0) setShares(String(sh));
            if (pr > 0) setCostPerShare(String(pr));
            try {
              document.querySelector('[data-testid="position-shares-input"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } catch { /* no-op */ }
          }}
        />
      )}

      {/* Add Position Form */}
      {isAllReadOnly ? (
        <div
          className="rounded-lg border bg-card p-3 space-y-2"
          data-testid="add-position-form-readonly"
        >
          <h3 className="text-sm font-bold flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-500" />
            <span>Lots you own across all portfolios</span>
          </h3>
          {positions.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              You don&rsquo;t own any shares of <span className="font-semibold text-foreground">{ticker}</span> in any of your portfolios yet.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              The lots above are your combined holdings across every portfolio.
            </p>
          )}
          <AllViewReadOnlyBanner verb="buy, sell, or change DRIP settings" />
        </div>
      ) : (
      <div className="rounded-lg border bg-card p-3 space-y-3" data-testid="add-position-form">
        <h3 className="text-sm font-bold">Add Position</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-muted-foreground">Shares Owned</label>
              {/* P4 (Feb 2026): Match Robinhood — reads the user's
                  clipboard and, if it parses as a positive number,
                  drops it into the shares field rounded to 4 decimals
                  (mirrors the backend's `_round_shares` clamp so the
                  visible total cost matches what actually persists).
                  Useful right after copying the share count from a
                  Robinhood trade screen. Falls back to a friendly
                  toast if clipboard access is denied (Safari private,
                  unfocused window, etc.). */}
              <button
                type="button"
                onClick={async () => {
                  try {
                    const raw = await navigator.clipboard.readText();
                    const cleaned = (raw || '').trim().replace(/[,$\s]/g, '');
                    const n = Number(cleaned);
                    if (!cleaned || !Number.isFinite(n) || n <= 0) {
                      toast.error('Clipboard didn\u2019t look like a share count', {
                        description: 'Copy the number from Robinhood (e.g. 1.234567) and try again.',
                      });
                      return;
                    }
                    const rounded = Math.round(n * 1e4) / 1e4;
                    setShares(String(rounded));
                    toast.success(`Pasted ${rounded} shares`, {
                      description: n !== rounded
                        ? `Rounded from ${n} to 4 decimals (DiviDrip storage precision).`
                        : 'Ready to submit.',
                    });
                  } catch {
                    toast.error('Couldn\u2019t read clipboard', {
                      description: 'Your browser blocked clipboard access. Paste the value manually instead.',
                    });
                  }
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-100 dark:hover:bg-cyan-950/60 ring-1 ring-cyan-300/50 dark:ring-cyan-700/40 transition-colors"
                data-testid="match-robinhood-btn"
                title="Paste the share count from your clipboard. Auto-clamps to 4 decimals."
              >
                <ClipboardCheck className="w-2.5 h-2.5" />
                Clipboard Paste
              </button>
            </div>
            <input
              type="number"
              min="0"
              step="0.0001"
              placeholder="# of shares"
              value={shares}
              onChange={e => setShares(e.target.value)}
              className="w-full h-10 rounded-lg border bg-background px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="position-shares-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Cost / Share ($)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Cost per share"
              value={costPerShare}
              onChange={e => setCostPerShare(e.target.value)}
              className="w-full h-10 rounded-lg border bg-background px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="position-cost-input"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Date Purchased</label>
          <DatePicker value={datePurchased} onChange={setDatePurchased} />
        </div>
        {/* DRIP Buy? — when YES, this lot is an informational-only
            record of past dividend reinvestment. Doesn't deduct from
            cash, doesn't count as a contribution. Useful when a brand
            new user is importing a portfolio with existing DRIP
            history before they've logged any cash.

            Round 97a (Feb 2026) — Hidden entirely for non-dividend
            stocks since there's no dividend cash to reinvest. */}
        {!isNonDiv && (
        <div data-testid="drip-buy-section">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-muted-foreground inline-flex items-center gap-1">
              <Droplets className="w-3.5 h-3.5 text-cyan-500" />
              DRIP Buy?
            </label>
            <span className="text-[10px] font-mono text-muted-foreground/70">
              {isDrip ? 'No cash deducted, no contribution' : 'Default: No'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { key: false, label: 'No' },
              { key: true, label: 'Yes' },
            ].map(opt => {
              // Selected colours:
              //   • No  → matches the footer disclaimer copy
              //           (amber-900 in light mode, yellow-400 in dark)
              //           so users tie the "default / safe" choice to
              //           the same neutral-warning palette they already
              //           recognise from the legal disclaimer.
              //   • Yes → cyan, mirrors the DRIP water-drop badge.
              const selectedClass = opt.key
                ? 'bg-cyan-600 text-white border-cyan-600'
                : 'bg-amber-900 text-yellow-50 border-amber-900 dark:bg-yellow-400 dark:text-yellow-950 dark:border-yellow-400';
              return (
                <button
                  key={String(opt.key)}
                  type="button"
                  onClick={() => setIsDrip(opt.key)}
                  className={`h-9 rounded-md border text-[11px] font-bold transition-colors ${
                    isDrip === opt.key
                      ? selectedClass
                      : 'bg-background border-border hover:border-primary/40'
                  }`}
                  data-testid={`drip-buy-${opt.key ? 'yes' : 'no'}`}
                >{opt.label}</button>
              );
            })}
          </div>
        </div>
        )}
        {/* Cash Source — only shown for non-DRIP buys. DRIP buys are
            informational-only and bypass cash bookkeeping entirely. */}
        {!isDrip && (
        <>
        <div data-testid="cash-source-section">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-muted-foreground">Cash source</label>
            <span className="text-[10px] font-mono text-muted-foreground" data-testid="cash-balance-available">
              Portfolio cash: <span className="font-bold text-foreground">${cashBalance.toFixed(2)}</span>
            </span>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {[
              { key: 'outside', label: 'Outside $', tip: 'Fresh money — counts as a new contribution.',
                selectedClass: 'bg-emerald-600 text-white border-emerald-600 dark:bg-emerald-500 dark:border-emerald-500' },
              { key: 'inside',  label: 'Inside $',  tip: 'Pay entirely from existing portfolio cash. Zero contribution.',
                selectedClass: 'bg-blue-600 text-white border-blue-600 dark:bg-blue-500 dark:border-blue-500' },
              // Split = half-and-half, green on the left (outside) and
              // blue on the right (inside). `from-50% to-50%` makes the
              // gradient transition zero-width so users see a clean
              // vertical split, not a blend.
              { key: 'split',   label: 'Split',     tip: 'Mix: some from inside, the rest from outside.',
                selectedClass: 'bg-gradient-to-r from-emerald-600 from-50% to-blue-600 to-50% text-white border-transparent dark:from-emerald-500 dark:to-blue-500' },
            ].map(opt => {
              const disabled = (opt.key === 'inside' || opt.key === 'split') && cashBalance <= 0;
              return (
                <button
                  key={opt.key}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    setCashSource(opt.key);
                    if (opt.key !== 'split') setInsideAmount('');
                  }}
                  className={`h-9 rounded-md border text-[11px] font-bold transition-colors ${
                    cashSource === opt.key
                      ? opt.selectedClass
                      : 'bg-background border-border hover:border-primary/40'
                  } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                  title={disabled ? 'No portfolio cash available' : opt.tip}
                  data-testid={`cash-source-${opt.key}`}
                >{opt.label}</button>
              );
            })}
          </div>
          {cashSource === 'split' && (
            <div className="mt-2">
              <label className="text-[10px] font-medium text-muted-foreground block mb-1">
                From inside cash ($)
                <span className="font-mono ml-1 text-muted-foreground/70">
                  · max ${Math.min(cashBalance, Number(shares || 0) * Number(costPerShare || 0)).toFixed(2)}
                </span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={insideAmount}
                onChange={e => setInsideAmount(e.target.value)}
                className="w-full h-9 rounded-lg border bg-background px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="inside-amount-input"
              />
              {Number(shares) > 0 && Number(costPerShare) > 0 && (() => {
                const totalCost = Number(shares) * Number(costPerShare);
                const inside = Math.max(0, Math.min(totalCost, Number(insideAmount) || 0));
                const outside = Math.max(0, totalCost - inside);
                return (
                  <p className="text-[10px] text-muted-foreground mt-1 font-mono" data-testid="cash-source-preview">
                    Total <span className="text-foreground font-bold">${totalCost.toFixed(2)}</span>{' '}
                    = inside <span className="text-orange-600 dark:text-orange-400 font-bold">${inside.toFixed(2)}</span>{' '}
                    + outside contribution <span className="text-emerald-600 dark:text-emerald-400 font-bold">${outside.toFixed(2)}</span>
                  </p>
                );
              })()}
            </div>
          )}
        </div>
        </>
        )}
        {isDrip && (
          <div className="rounded-md border border-cyan-300 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-950/40 p-2.5 text-[11px] text-cyan-800 dark:text-cyan-300" data-testid="drip-buy-explainer">
            <span className="font-bold">DRIP buy logged.</span> No cash deducted, not counted as a contribution. Cost basis still recorded for Yield-on-Cost calculations.
          </div>
        )}
        <div data-testid="add-position-account-type-row">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
            Account Type
          </label>
          <select
            value={acctType}
            onChange={(e) => setAcctType(e.target.value)}
            className="w-full h-10 rounded-lg border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="add-position-account-type-select"
          >
            {ACCOUNT_TYPES.map((at) => (
              <option key={at.key} value={at.key}>
                {at.label}{!at.niit_applies ? ' — NIIT-exempt' : ''}
              </option>
            ))}
          </select>
          <p className="text-[10px] font-bold text-muted-foreground mt-1">
            Drives the NIIT exposure tile on your Year-End Report. Defaults to Taxable Brokerage.
          </p>
        </div>
        <div data-testid="add-position-broker-row">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
            Brokerage <span className="text-[10px] normal-case font-normal text-muted-foreground/70">(optional)</span>
          </label>
          <select
            value={broker}
            onChange={(e) => setBroker(e.target.value)}
            className="w-full h-10 rounded-lg border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="add-position-broker-select"
          >
            <option value="">— Untagged —</option>
            {BROKERS.map((br) => (
              <option key={br.key} value={br.key}>{br.label}</option>
            ))}
          </select>
          <p className="text-[10px] font-bold text-muted-foreground mt-1">
            Rolls up your portfolio per-broker on the Tax Account Audit. Leave Untagged if you don&rsquo;t track this.
          </p>
        </div>
        <Button
          className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500 dark:hover:bg-blue-400 dark:text-white"
          disabled={!shares || Number(shares) <= 0 || submitting}
          onClick={handleAdd}
          data-testid="add-position-btn"
        >
          {submitting ? 'Adding...' : 'Add Position'}
        </Button>
      </div>
      )}

      {/* Summary Info Line */}
      {!quickMode && metrics && metrics.totalShares > 0 && (
        <>
          <p className="text-xs font-bold text-center text-yellow-600 dark:text-yellow-400" data-testid="stock-info-line">
            Current price: ${metrics.price.toFixed(2)} · Yield: {metrics.divYield.toFixed(2)}% · Annual dividend: ${metrics.divRate.toFixed(2)}
          </p>

          {/* Metric Cards */}
          <div className="grid grid-cols-3 gap-2">
            <SummaryCard
              icon={DollarSign}
              label="Cost Basis"
              value={`$${metrics.totalCost.toFixed(2)}`}
              subtitle={`avg $${metrics.avgCost.toFixed(2)}/share`}
              testId="metric-cost-basis"
            />
            <SummaryCard
              icon={TrendingUp}
              label="Current Value"
              value={`$${metrics.currentValue.toFixed(2)}`}
              subtitle={`${metrics.totalShares.toFixed(2)} @ $${metrics.price.toFixed(2)}`}
              testId="metric-current-value"
            />
            <SummaryCard
              icon={BarChart3}
              label="Gain/Loss"
              value={`${metrics.gainLoss >= 0 ? '+' : ''}$${metrics.gainLoss.toFixed(2)}`}
              subtitle={`${metrics.gainLossPct >= 0 ? '+' : ''}${metrics.gainLossPct.toFixed(2)}%`}
              valueColor={metrics.gainLoss >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}
              testId="metric-gain-loss"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <SummaryCard
              icon={DollarSign}
              label="Annual Income"
              value={`$${metrics.annualIncome.toFixed(2)}`}
              subtitle={`$${metrics.monthlyIncome.toFixed(2)}/month`}
              valueColor="text-green-600 dark:text-green-400"
              testId="metric-annual-income"
            />
            <SummaryCard
              icon={DollarSign}
              label="Per Dividend"
              value={`$${metrics.perDividend.toFixed(2)}`}
              subtitle={`per ${metrics.freq.toLowerCase()}`}
              testId="metric-per-dividend"
            />
            <SummaryCard
              icon={Percent}
              label="Yield on Cost"
              value={`${metrics.yieldOnCost.toFixed(2)}%`}
              subtitle="on cost basis"
              valueColor="text-green-600 dark:text-green-400"
              testId="metric-yield-on-cost"
            />
          </div>
        </>
      )}

      {/* Empty State */}
      {!quickMode && (positions.length === 0 && !shares) && (
        <div className="rounded-lg border bg-card p-8 text-center" data-testid="empty-portfolio">
          <DollarSign className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Add a position above to see your portfolio details</p>
        </div>
      )}

      {/* Round 91q — Snapshot share mini-CTA. Logged-in users sharing a
          single ticker with a friend can grab the public Snapshot URL
          in one click — no need to remember the /stock/:ticker pattern.
          Renders at the bottom of the My Portfolio tab below all position
          actions so it stays out of the way during normal use. */}
      {!quickMode && stock?.ticker && (
        <SnapshotShareCTA ticker={stock.ticker} stockName={stock.name} onOpen={onOpenSnapshot} />
      )}

      {/* Round 71l: post-trade journal entry modal. Spawned by the
          "Journal it" pill on the post-buy / post-sell suggest toast. */}
      {journalPrefill && (
        <JournalEntryModal
          prefill={journalPrefill}
          onClose={() => setJournalPrefill(null)}
          onSaved={() => setJournalPrefill(null)}
        />
      )}

      {/* Round 95v (Jun 2026) — per-lot account-type + broker editors.
          Open when a row's badge is clicked. Mirrors PortfolioView. */}
      {acctEditing && (
        <AccountTypeModal
          open={true}
          ticker={ticker}
          value={acctEditing.account_type}
          onClose={() => setAcctEditing(null)}
          onSave={async (newType, opts) => {
            await _saveAccountType(acctEditing, newType, opts);
            setAcctEditing(null);
          }}
          title={`Account for ${acctEditing.shares} sh of ${ticker}`}
        />
      )}
      {brokerEditing && (
        <BrokerModal
          open={true}
          ticker={ticker}
          value={brokerEditing.broker}
          onClose={() => setBrokerEditing(null)}
          onSave={async (newBroker, opts) => {
            await _saveBroker(brokerEditing, newBroker, opts);
            setBrokerEditing(null);
          }}
          title={`Broker for ${brokerEditing.shares} sh of ${ticker}`}
        />
      )}
    </div>
  );
}

/**
 * SnapshotShareCTA — Round 91q (Feb 2026).
 *
 * Tiny pill row that copies the public /stock/:ticker URL to the
 * clipboard so the user can paste it into a chat / Reddit / X / email
 * without remembering the canonical pattern. Lives at the bottom of
 * the My Portfolio tab inside the Stock Modal.
 */
function SnapshotShareCTA({ ticker, stockName, onOpen }) {
  const [copied, setCopied] = useState(false);
  const url = `https://twylightcrow.com/stock/${ticker}`;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(`Copied ${ticker} Snapshot link`);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); toast.success(`Copied ${ticker} Snapshot link`); setTimeout(() => setCopied(false), 2200); } catch { toast.error('Could not copy — long-press the link instead'); }
      document.body.removeChild(ta);
    }
  };
  return (
    <div
      className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 dark:bg-amber-500/10 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
      data-testid="snapshot-share-cta"
    >
      <div className="flex items-center gap-2 text-xs">
        <Camera className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
        <span className="text-foreground">
          <strong>Share {ticker}</strong>
          {stockName ? <span className="text-muted-foreground"> ({stockName})</span> : null}
          {' '}— send a friend the public Snapshot page.
        </span>
      </div>
      <div className="flex items-center gap-2">
        <a
          href={`/stock/${ticker}`}
          onClick={(e) => {
            // Round 91r — open in same tab; the modal-restore wrapper
            // passed in from StockModal closes the modal + encodes
            // modalOrigin so Back returns the user to this exact spot.
            if (typeof onOpen === 'function') {
              e.preventDefault();
              onOpen();
            }
          }}
          className="text-[11px] underline underline-offset-2 text-sky-700 dark:text-sky-300 hover:text-sky-600"
          data-testid="snapshot-share-cta-open"
        >
          Open Snapshot
        </a>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopy}
          data-testid="snapshot-share-cta-copy"
          className={`gap-1.5 text-xs h-7 px-2.5 ${copied ? 'border-emerald-500/60 text-emerald-700 dark:text-emerald-300' : 'border-amber-500/50 text-amber-700 dark:text-amber-300'}`}
          title={copied ? 'Copied!' : 'Copy link to clipboard'}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied' : 'Copy link'}
        </Button>
      </div>
    </div>
  );
}
